import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useCreateServiceMutation,
  useGetServiceCategoriesQuery,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import ImageUpload from "../components/ImageUpload";
import Icon from "../components/Icon";
import RichTextEditor from "../components/RichTextEditor";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const AddService = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [saveStatus, setSaveStatus] = useState("");
  const { t } = useTranslation();
  const messageType = useMessageType(saveStatus);

  // RTK Query hooks
  const [createService, { isLoading: saving }] = useCreateServiceMutation();
  const { data: categoriesData, isLoading: loadingCategories } =
    useGetServiceCategoriesQuery();

  const categories = categoriesData?.categories || [];

  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    content: "",
    contentAr: "",
    image: null,
    category_id: "",
    is_active: true,
    keywords: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("addEditService.pleaseLoginToSave"));
      return;
    }

    // Validate form
    if (!formData.title.trim()) {
      setSaveStatus(t("addEditService.pleaseEnterTitle"));
      return;
    }

    if (!formData.content.trim()) {
      setSaveStatus(t("addEditService.pleaseEnterContent"));
      return;
    }

    if (!formData.category_id) {
      setSaveStatus(t("addEditService.pleaseSelectCategory"));
      return;
    }

    setSaveStatus("");

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("titleAr", formData.titleAr);
      submitData.append("content", formData.content);
      submitData.append("contentAr", formData.contentAr);
      submitData.append("category_id", formData.category_id);
      submitData.append("is_active", formData.is_active ? "1" : "0");
      submitData.append("keywords", formData.keywords || "");

      // Only append image if it's a file
      if (formData.image && typeof formData.image === "object") {
        submitData.append("image", formData.image);
      }

      const result = await createService(submitData).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditService.serviceCreatedSuccessfully"));
        setTimeout(() => {
          navigate("/dashboard/services");
        }, 1500);
      } else {
        setSaveStatus(t("addEditService.errorCreatingService"));
      }
    } catch (error) {
      console.error("Error creating service:", error);
      setSaveStatus(
        error.data?.message || t("addEditService.errorCreatingService")
      );
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/services");
  };

  if (!isAuthenticated) {
    return (
      <div className="add-service">
        <div className="auth-required">
          <h2>{t("addEditService.authenticationRequired")}</h2>
          <p>{t("addEditService.pleaseLoginToCreate")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("addEditService.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-service">
      <div className="page-header">
        <div className="header-content">
          <button type="button" className="back-btn" onClick={handleCancel}>
            <Icon name="arrow-left" size="16px" />
            {t("addEditService.backToServices")}
          </button>
          <h1>{t("addEditService.addNewService")}</h1>
          <p>{t("addEditService.createNewService")}</p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">
          <div className="form-content">
            <div className="form-group">
              <label>{t("addEditService.serviceTitleEnglish")} *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("addEditService.enterServiceTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceTitleArabic")}</label>
              <input
                type="text"
                value={formData.titleAr}
                onChange={(e) => handleInputChange("titleAr", e.target.value)}
                placeholder={t("addEditService.enterServiceTitleArabic")}
                className="form-input"
                dir="rtl"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceCategory")} *</label>
              <select
                value={formData.category_id}
                onChange={(e) =>
                  handleInputChange("category_id", e.target.value)
                }
                className="form-input"
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories
                    ? t("servicesDashboard.loading")
                    : t("addEditService.selectCategory")}
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <ImageUpload
                label={t("addEditService.serviceImage")}
                value={formData.image}
                onChange={(file) => handleInputChange("image", file)}
                className="image-upload-modern"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceContentEnglish")} *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => handleInputChange("content", content)}
                placeholder={t("addEditService.enterServiceDetails")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceContentArabic")}</label>
              <RichTextEditor
                value={formData.contentAr}
                onChange={(content) => handleInputChange("contentAr", content)}
                placeholder={t("addEditService.enterServiceDetailsArabic")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.keywords")}</label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => handleInputChange("keywords", e.target.value)}
                placeholder={t("addEditService.enterKeywords")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.keywordsHint")}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                />
                <span>{t("addEditService.activeService")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus("")}
        />
        <div className="save-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            {t("addEditService.cancel")}
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                {t("addEditService.creating")}
              </>
            ) : (
              <>
                <Icon name="save" size="16px" />
                {t("addEditService.createService")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddService;
